import { organizationDefinition } from "../organization";
import { questionAnswerIssues } from "../response";
import {
  repeatFieldsOf,
  repeatFieldIssueMessage,
  entryLabelOf,
  maxEntriesOf,
  minEntriesOf,
  fieldQuestion,
  visibleEntryFields,
  visibleRepeatedAnswer,
} from "../repeatable";
import { z } from "zod";
import type {
  AnswerValue,
  Choice,
  Question,
  QuestionConfig,
  QuestionOption,
  QuestionType,
  ValidationRule,
} from "../types";
import { parseMoney, formatMoneyAnswer } from "../money";
import {
  QUESTION_TYPES,
  repeatableAnswerSchema,
  isBlankEntry,
  entryValue,
} from "../types";
import { byPos } from "../sortKey";

/**
 * One definition per question type — the single place that knows what a type
 * is called, what settings it takes, what shape its answer has, which
 * validation rules it offers, and how its answer reads as plain text.
 *
 * The alternative is a `switch (question.type)` in the builder, another in the
 * renderer, another in the validator and another in the exporter; the fifth one
 * is always the one someone forgets. Adding a type here is one entry.
 *
 * React components live in `../react/registry` so this stays importable from a
 * server or worker.
 */
export interface QuestionTypeDefinition {
  type: QuestionType;
  /** What an admin picks it by. */
  label: string;
  /** One line in the type picker. */
  hint: string;
  /** Settings a freshly added question of this type starts with. */
  defaultConfig: QuestionConfig;
  /**
   * The options a freshly added question of this type starts with, if it is a
   * choice type at all.
   *
   * Labels only — ids are minted per question when the options are created, so
   * that two questions never share one. Its presence, not the shape of
   * `defaultConfig`, is what says "this type has options": inferring that from
   * example data is how the linear scale ended up with settings no one could
   * reach.
   */
  defaultOptionLabels?: readonly string[];
  /**
   * The rows a freshly added question of this type starts with — grids only.
   * Same contract as `defaultOptionLabels`: labels here, ids minted per
   * question, and its presence is what says "this type has rows".
   */
  defaultRowLabels?: readonly string[];
  /** Validation rules this type can offer in the inspector. */
  validators: readonly ValidationRule["kind"][];
  /** The answer shape, given the question's settings. */
  answerSchema(question: Question): z.ZodType<AnswerValue>;
  /**
   * What `required` demands beyond "not empty", if the type's answer can be
   * present but incomplete. A grid is the case in point: its schema accepts a
   * partly answered value on purpose — the value has to be storable mid-fill —
   * so "every row answered" can only live on the required check. Returns the
   * complaint, or null when the answer satisfies it.
   *
   * On the definition rather than in the validator so a future composite type
   * carries its own answer to "what does required mean here" instead of
   * relying on someone remembering to extend a switch elsewhere.
   */
  requiredIssue?(value: AnswerValue, question: Question): string | null;
  /**
   * The answer is written only by dedicated server routes, never by the
   * applicant's save batch. A file answer names storage the server owns:
   * letting the client write, replace or clear it would detach objects from
   * the lifecycle that deletes them. The save route refuses *every* change
   * aimed at such a question, whatever the value looks like.
   */
  serverWrittenAnswer?: boolean;
  /**
   * How this answer reads as plain text.
   *
   * Not optional decoration: everything this form feeds eventually reads
   * answers as text, so a type without a defined rendering degrades silently
   * downstream. Defining it costs nothing when the type is written.
   */
  toText(value: AnswerValue | undefined, question: Question): string;
}

const textAnswer = z.object({
  kind: z.literal("text"),
  value: z.string(),
}) as unknown as z.ZodType<AnswerValue>;

/**
 * The options a question actually offers, in display order.
 *
 * An option whose label is still blank is not a choice anyone can make — it
 * can't be validated against and rendering one as a select item throws — so
 * every consumer must come through here rather than reading `config.options`
 * directly. Half-typed options are normal in a live builder, not an error.
 */
export function optionsOf(question: Question): QuestionOption[] {
  return (question.config?.options ?? [])
    .filter((option) => option.label.trim() !== "")
    .map(normalized)
    .sort(byPos);
}

/**
 * An option as everything outside the builder should see it.
 *
 * The label is trimmed, keeping the id and sort position: the builder stores
 * exactly what was typed so an admin's cursor doesn't jump around mid-edit,
 * but padding is not part of what the option *says*. Everything downstream
 * reads answers as text, so a stray space would follow an answer all the way
 * into an export.
 */
function normalized(option: QuestionOption): QuestionOption {
  const label = option.label.trim();
  return label === option.label ? option : { ...option, label };
}

/**
 * A grid's rows, in display order — the row-shaped sibling of `optionsOf`,
 * with the same rule: a row whose label is still blank isn't a row anyone can
 * be asked about, so consumers come through here rather than reading
 * `config.rows` directly.
 */
export function rowsOf(question: Question): QuestionOption[] {
  return (question.config?.rows ?? [])
    .filter((row) => row.label.trim() !== "")
    .map(normalized)
    .sort(byPos);
}

/** An option by id, or undefined if it has since been deleted. */
export function optionById(
  question: Question,
  optionId: string,
): QuestionOption | undefined {
  const found = question.config?.options?.find(
    (option) => option.id === optionId,
  );
  return found && normalized(found);
}

/** How one selection reads as text — the option's label, or the typed value. */
export function choiceToText(question: Question, choice: Choice): string {
  if (choice.kind === "other") return choice.value;
  return optionById(question, choice.optionId)?.label ?? "";
}

/**
 * One selection, constrained to options this question still offers.
 *
 * Checked against ids rather than labels: an answer records which option was
 * picked, and renaming that option later must not invalidate it.
 */
function choiceOf(question: Question): z.ZodType<Choice> {
  const ids = new Set(optionsOf(question).map((option) => option.id));
  return z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("option"),
      optionId: z.string().refine((id) => ids.has(id), {
        message: "Choose one of the offered options",
      }),
    }),
    z.object({
      kind: z.literal("other"),
      // Free text is only a valid answer where the question invites it, and
      // spaces are not an answer: `isEmptyValue` trims before deciding whether
      // a question was answered, so accepting whitespace here would store
      // something the required and visibility rules both call blank.
      value: offersOther(question)
        ? z
            .string()
            .trim()
            .min(1, { message: "Type an answer, or pick an option" })
        : z.never(),
    }),
  ]) as unknown as z.ZodType<Choice>;
}

function choiceAnswer(question: Question): z.ZodType<AnswerValue> {
  return z.object({
    kind: z.literal("choice"),
    choice: choiceOf(question),
  }) as unknown as z.ZodType<AnswerValue>;
}

function choicesAnswer(question: Question): z.ZodType<AnswerValue> {
  return z.object({
    kind: z.literal("choices"),
    // Distinct: a repeated entry would satisfy "choose at least two" with one
    // option ticked, since the count is taken from the list, and a reviewer
    // would see the same answer twice. At most one "other", because that is
    // one text box rather than a repeatable choice.
    choices: z
      .array(choiceOf(question))
      .refine(
        (choices) => {
          const ids = choices
            .filter((choice) => choice.kind === "option")
            .map((choice) => choice.optionId);
          return new Set(ids).size === ids.length;
        },
        { message: "Each option can only be chosen once" },
      )
      .refine(
        (choices) =>
          choices.filter((choice) => choice.kind === "other").length <= 1,
        { message: "Only one typed answer" },
      ),
  }) as unknown as z.ZodType<AnswerValue>;
}

/** Per-file size limit a file question gets when it doesn't set one. */
export const DEFAULT_MAX_FILE_SIZE_MB = 25;
/** The smallest per-file limit an author may set. */
export const MIN_FILE_SIZE_MB = 1;
/** The largest per-file limit an author may set. Bounds one upload request. */
export const MAX_FILE_SIZE_MB_CEILING = 100;
/** The most files one answer may hold, whatever the author asks for. */
export const MAX_FILES_CEILING = 10;

/**
 * The bounds above, applied. Anything that *produces* a file setting from
 * outside the builder — the Google Form importer today, an API tomorrow —
 * goes through these, and the publish check judges against the same
 * constants, so a produced value can never be one publishing refuses.
 */
export function clampMaxFiles(count: number): number {
  return Math.min(MAX_FILES_CEILING, Math.max(1, Math.round(count)));
}
export function clampMaxFileSizeMb(mb: number): number {
  return Math.min(MAX_FILE_SIZE_MB_CEILING, Math.max(MIN_FILE_SIZE_MB, mb));
}

/**
 * Whether `contentType` satisfies one entry of a question's `acceptedTypes`.
 * Exact match, or a `type/*` wildcard — the same two forms an HTML `accept`
 * attribute takes, so the builder's setting, the browser's file picker, and
 * the server all enforce one rule.
 */
export function matchesAcceptedType(
  contentType: string,
  accepted: string,
): boolean {
  const type = contentType.toLowerCase();
  const pattern = accepted.toLowerCase();
  if (pattern.endsWith("/*")) return type.startsWith(pattern.slice(0, -1));
  return type === pattern;
}

/**
 * What a browser would have said a file is, when it said nothing. `File.type`
 * comes from the OS's extension registry, and a machine without Office or a
 * Linux desktop with a thin registry hands back "" for a perfectly good
 * .docx — which a type-restricted question must not refuse on sight. The
 * same extension→type map the OS would have consulted fills the gap, for
 * exactly the types authors can restrict to; anything else is declared as
 * opaque bytes. The bytes are judged on the server either way, so this only
 * decides what the file CLAIMS to be, never what it is.
 */
const EXTENSION_TYPES: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  csv: "text/csv",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  svg: "image/svg+xml",
  txt: "text/plain",
  md: "text/markdown",
  json: "application/json",
  zip: "application/zip",
};

export const UNKNOWN_CONTENT_TYPE = "application/octet-stream";

/** The type a picked file declares: the browser's, or the extension's, or unknown. */
export function declaredContentType(file: {
  name: string;
  type: string;
}): string {
  if (file.type) return file.type;
  const dot = file.name.lastIndexOf(".");
  const extension = dot === -1 ? "" : file.name.slice(dot + 1).toLowerCase();
  return EXTENSION_TYPES[extension] ?? UNKNOWN_CONTENT_TYPE;
}

/** An omitted or empty `acceptedTypes` means any type (see the config schema). */
export function isAcceptedType(
  contentType: string,
  acceptedTypes: readonly string[] | undefined,
): boolean {
  if (!acceptedTypes || acceptedTypes.length === 0) return true;
  return acceptedTypes.some((accepted) =>
    matchesAcceptedType(contentType, accepted),
  );
}

/**
 * The type restrictions an author picks from. Stored as the list itself, so
 * a question keeps meaning what it meant if a preset is later edited; a
 * stored list that matches no preset reads back as "Custom".
 */
export const FILE_TYPE_PRESETS: readonly {
  key: string;
  label: string;
  types: readonly string[] | undefined;
}[] = [
  { key: "any", label: "Any file", types: undefined },
  { key: "pdf", label: "PDF only", types: ["application/pdf"] },
  {
    key: "documents",
    label: "Documents",
    types: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  },
  { key: "images", label: "Images", types: ["image/*"] },
  {
    key: "spreadsheets",
    label: "Spreadsheets",
    types: [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
  },
  {
    key: "presentations",
    label: "Presentations",
    types: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
  },
];

/** The preset a stored `acceptedTypes` list came from, or null for a custom list. */
export function fileTypePresetOf(
  acceptedTypes: readonly string[] | undefined,
): string | null {
  const stored = [...(acceptedTypes ?? [])].sort().join("\n");
  const preset = FILE_TYPE_PRESETS.find(
    (candidate) => [...(candidate.types ?? [])].sort().join("\n") === stored,
  );
  return preset?.key ?? null;
}

/**
 * How many files this question's answer may hold. Absence means one — the
 * field is optional and Google Form imports can omit it — and this is the
 * one place that says so.
 */
export function maxFilesOf(question: Question): number {
  return question.config?.maxFiles ?? 1;
}

/**
 * This question's per-file size limit in MB. Absence means the default,
 * never "unbounded": the upload routes compare against this, and a
 * comparison against `undefined` would let anything through.
 */
export function maxFileSizeMbOf(question: Question): number {
  return question.config?.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
}

const fileRef = z.object({
  fileId: z.string().min(1),
  filename: z.string().min(1),
});

function filesAnswerFor(question: Question): z.ZodType<AnswerValue> {
  const max = maxFilesOf(question);
  return z.object({
    kind: z.literal("files"),
    files: z
      .array(fileRef)
      .max(max, {
        message:
          max === 1 ? "Attach one file at most" : `Attach up to ${max} files`,
      })
      // Distinct: the same file listed twice would count twice toward the
      // limit and show a reviewer the same attachment twice.
      .refine(
        (files) =>
          new Set(files.map((file) => file.fileId)).size === files.length,
        { message: "Each file can only be attached once" },
      ),
  }) as unknown as z.ZodType<AnswerValue>;
}

const TEXT_VALIDATORS = [
  "minLength",
  "maxLength",
  "email",
  "url",
  "regex",
  "contains",
  "number",
  "integer",
  "min",
  "max",
] as const;

function plainText(value: AnswerValue | undefined, question: Question): string {
  if (!value) return "";
  switch (value.kind) {
    case "text":
      return value.value;
    case "number":
      return String(value.value);
    case "choice":
      return choiceToText(question, value.choice);
    case "choices":
      return value.choices
        .map((choice) => choiceToText(question, choice))
        .join(", ");
    case "files":
      return value.files.map((file) => file.filename).join(", ");
    case "organization":
    case "entries":
    case "grid":
      // Composite values have their own type-specific text rendering.
      return "";
  }
}

export const QUESTION_TYPE_DEFINITIONS: Record<
  QuestionType,
  QuestionTypeDefinition
> = {
  organization: organizationDefinition,
  short_answer: {
    type: "short_answer",
    label: "Short answer",
    hint: "A single line of text",
    defaultConfig: {},
    validators: TEXT_VALIDATORS,
    answerSchema: () => textAnswer,
    toText: plainText,
  },
  money: {
    type: "money",
    label: "Money (USD)",
    hint: "A non-negative US dollar amount, with up to two decimal places",
    defaultConfig: {},
    validators: [],
    answerSchema: () =>
      z.object({
        kind: z.literal("text"),
        value: z.string().refine((text) => parseMoney(text) !== undefined, {
          message:
            "Enter a US dollar amount of zero or more, with up to two decimal places (for example, 25,000.50).",
        }),
      }),
    toText: (value) =>
      value?.kind === "text" ? formatMoneyAnswer(value.value) : "",
  },
  paragraph: {
    type: "paragraph",
    label: "Paragraph",
    hint: "Several lines of text",
    defaultConfig: {},
    validators: ["minLength", "maxLength", "regex", "contains"],
    answerSchema: () => textAnswer,
    toText: plainText,
  },
  multiple_choice: {
    type: "multiple_choice",
    label: "Multiple choice",
    hint: "Pick one option",
    defaultConfig: { allowOther: false, shuffle: false },
    defaultOptionLabels: ["Option 1", "Option 2"],
    validators: [],
    answerSchema: choiceAnswer,
    toText: plainText,
  },
  checkboxes: {
    type: "checkboxes",
    label: "Checkboxes",
    hint: "Pick any number of options",
    defaultConfig: { allowOther: false, shuffle: false },
    defaultOptionLabels: ["Option 1", "Option 2"],
    validators: ["minSelected", "maxSelected"],
    answerSchema: choicesAnswer,
    toText: plainText,
  },
  dropdown: {
    type: "dropdown",
    label: "Dropdown",
    hint: "Pick one option from a list",
    defaultConfig: { shuffle: false },
    defaultOptionLabels: ["Option 1", "Option 2"],
    validators: [],
    answerSchema: choiceAnswer,
    toText: plainText,
  },
  date: {
    type: "date",
    label: "Date",
    hint: "A calendar date",
    defaultConfig: {},
    validators: [],
    answerSchema: (question) => {
      const monthDayOnly = question.config?.monthDayOnly === true;
      const includeTime = question.config?.includeTime === true;
      // Zod's ISO validators do the real calendar and clock checking — a
      // lookalike regex here accepted 2026-99-99T99:99. Minutes required,
      // seconds tolerated, same reasoning as the time question below.
      const full = includeTime ? z.iso.datetime({ local: true }) : z.iso.date();
      // `--MM-DD` is ISO 8601's yearless date, so a month-and-day answer is
      // still unmistakably not a full date to anything reading it later. It
      // borrows the full validator by pinning year 2000 — a leap year, so
      // `--02-29` stays a real birthday.
      const value = monthDayOnly
        ? z
            .string()
            .refine(
              (answer) =>
                (includeTime ? /^--\d{2}-\d{2}T/ : /^--\d{2}-\d{2}$/).test(
                  answer,
                ) && full.safeParse(`2000-${answer.slice(2)}`).success,
              {
                message: includeTime
                  ? "Give a month, day and time"
                  : "Give a month and day",
              },
            )
        : full;
      return z.object({
        kind: z.literal("text"),
        value,
      }) as unknown as z.ZodType<AnswerValue>;
    },
    toText: plainText,
  },
  time: {
    type: "time",
    label: "Time",
    hint: "A time of day, or an amount of time",
    defaultConfig: {},
    validators: [],
    answerSchema: (question) =>
      z.object({
        kind: z.literal("text"),
        // Both "14:30" and "14:30:00": a time input yields minutes normally and
        // seconds when its step asks for them, and the difference is meaningless
        // to the question. Pinning to minutes rejected perfectly good answers.
        // A duration reads the same but the hours aren't bounded by a clock.
        value:
          question.config?.duration === true
            ? z.string().regex(/^\d{1,3}:[0-5]\d(:[0-5]\d)?$/, {
                message: "Give an amount of time as hours:minutes",
              })
            : z.iso.time(),
      }) as unknown as z.ZodType<AnswerValue>,
    toText: plainText,
  },
  file_upload: {
    type: "file_upload",
    label: "File upload",
    hint: "Attached files",
    defaultConfig: { maxFileSizeMb: DEFAULT_MAX_FILE_SIZE_MB },
    validators: [],
    answerSchema: filesAnswerFor,
    serverWrittenAnswer: true,
    // The reviewer sees the filenames; where the bytes live is storage detail.
    toText: plainText,
  },
  linear_scale: {
    type: "linear_scale",
    label: "Linear scale",
    hint: "A number between two labelled ends",
    defaultConfig: { min: 1, max: 5, minLabel: "", maxLabel: "" },
    validators: [],
    answerSchema: (question) => {
      const min = question.config?.min ?? 1;
      const max = question.config?.max ?? 5;
      return z.object({
        kind: z.literal("number"),
        value: z.number().int().min(min).max(max),
      }) as unknown as z.ZodType<AnswerValue>;
    },
    toText: (value, question) => {
      if (!value || value.kind !== "number") return "";
      const max = question.config?.max ?? 5;
      return `${value.value} out of ${max}`;
    },
  },
  rating: {
    type: "rating",
    label: "Rating",
    hint: "A count of stars, hearts or thumbs",
    // The scale always starts at 1 — `max` is the whole setting. Sharing the
    // key with the linear scale means switching between the two keeps the
    // author's chosen top end.
    defaultConfig: { max: 5, ratingIcon: "star" },
    validators: [],
    answerSchema: (question) => {
      return z.object({
        kind: z.literal("number"),
        value: z.number().int().min(1).max(ratingMax(question)),
      }) as unknown as z.ZodType<AnswerValue>;
    },
    toText: (value, question) => {
      if (!value || value.kind !== "number") return "";
      const max = ratingMax(question);
      const icon = question.config?.ratingIcon ?? "star";
      const noun =
        icon === "heart" ? "hearts" : icon === "thumb" ? "thumbs up" : "stars";
      return `${value.value} of ${max} ${noun}`;
    },
  },
  grid: {
    type: "grid",
    label: "Grid",
    hint: "Rows of things rated against shared columns",
    defaultConfig: { multiSelect: false, shuffle: false },
    // The columns are the grid's options — same key, same minted ids — so a
    // choice question turned into a grid keeps the ids its answers and
    // conditions already point at.
    defaultOptionLabels: ["Column 1", "Column 2"],
    defaultRowLabels: ["Row 1", "Row 2"],
    validators: [],
    answerSchema: (question) => {
      const rowIds = new Set(rowsOf(question).map((row) => row.id));
      const columnIds = new Set(optionsOf(question).map((option) => option.id));
      const multi = question.config?.multiSelect === true;
      return z.object({
        kind: z.literal("grid"),
        rows: z
          .array(
            z.object({
              rowId: z.string().refine((id) => rowIds.has(id), {
                message: "Answer rows the grid offers",
              }),
              optionIds: z
                .array(
                  z.string().refine((id) => columnIds.has(id), {
                    message: "Choose one of the offered columns",
                  }),
                )
                .min(1)
                .refine((ids) => new Set(ids).size === ids.length, {
                  message: "Each column can only be chosen once per row",
                })
                .refine((ids) => multi || ids.length === 1, {
                  message: "Choose one column per row",
                }),
            }),
          )
          .refine(
            (rows) =>
              new Set(rows.map((row) => row.rowId)).size === rows.length,
            { message: "Each row can only be answered once" },
          ),
      }) as unknown as z.ZodType<AnswerValue>;
    },
    // A required grid means every row answered: the schema accepts a partial
    // value so mid-fill state is storable, and `isEmptyValue` counts any
    // answered row so `isAnswered` conditions behave — this is the one check
    // left to say "all of it".
    requiredIssue: (value, question) => {
      if (value.kind !== "grid") return null;
      const answered = new Set(value.rows.map((row) => row.rowId));
      return rowsOf(question).every((row) => answered.has(row.id))
        ? null
        : "Answer every row.";
    },
    // One line per answered row: "Row: Column, Column". Unanswered rows are
    // absent from the value and so from the text.
    toText: (value, question) => {
      if (!value || value.kind !== "grid") return "";
      const rowLabel = new Map(
        rowsOf(question).map((row) => [row.id, row.label]),
      );
      return value.rows
        .map((row) => {
          const columns = row.optionIds
            .map((id) => optionById(question, id)?.label ?? "")
            .filter((label) => label !== "")
            .join(", ");
          return `${rowLabel.get(row.rowId) ?? ""}: ${columns}`;
        })
        .join("\n");
    },
  },
  repeatable_group: {
    type: "repeatable_group",
    label: "Repeatable group",
    hint: "A set of fields applicants can fill in more than once",
    defaultConfig: {
      entryLabel: "Entry",
      minEntries: 0,
      fields: [
        {
          id: "field_default",
          label: "Name",
          type: "short_answer",
          required: true,
          pos: "a0",
        },
      ],
    },
    validators: [],
    answerSchema: (question) =>
      repeatableAnswerSchema
        .superRefine((answer, ctx) => {
          const entries = answer.entries.filter(
            (entry) => !isBlankEntry(entry),
          );
          const min = minEntriesOf(question);
          if (entries.length < min)
            ctx.addIssue({
              code: "custom",
              message: `Add at least ${min} entries.`,
            });
          if (entries.length > maxEntriesOf(question))
            ctx.addIssue({
              code: "custom",
              message: `Add no more than ${maxEntriesOf(question)} entries.`,
            });
          const fields = repeatFieldsOf(question);
          const ids = new Set(fields.map((field) => field.id));
          for (const [index, entry] of answer.entries.entries()) {
            if (Object.keys(entry.values).some((id) => !ids.has(id)))
              ctx.addIssue({
                code: "custom",
                message: "This entry contains a field no longer in the group.",
              });
            if (isBlankEntry(entry)) continue;
            for (const field of visibleEntryFields(question, entry)) {
              for (const message of questionAnswerIssues(
                fieldQuestion(field),
                entryValue(entry, field.id),
              ))
                ctx.addIssue({
                  code: "custom",
                  message: repeatFieldIssueMessage(
                    question,
                    index,
                    field,
                    message,
                  ),
                });
            }
          }
        })
        .transform((answer) => ({
          ...answer,
          entries: answer.entries
            .filter((entry) => !isBlankEntry(entry))
            .map((entry) => ({
              ...entry,
              values: Object.fromEntries(
                visibleEntryFields(question, entry).flatMap((field) => {
                  const value = entry.values[field.id];
                  return value === undefined ? [] : [[field.id, value]];
                }),
              ),
            })),
        })),
    toText: (value, question) =>
      value?.kind === "entries"
        ? value.entries
            .filter((entry) => !isBlankEntry(entry))
            .map((entry, index) =>
              [
                `${entryLabelOf(question)} ${index + 1}`,
                ...visibleEntryFields(question, entry).flatMap((field) => {
                  const text = answerToText(
                    fieldQuestion(field),
                    entryValue(entry, field.id),
                  );
                  return text ? [`${field.label}: ${text}`] : [];
                }),
              ].join("\n"),
            )
            .join("\n\n")
        : "",
  },
  text_block: {
    type: "text_block",
    label: "Text block",
    hint: "A heading and text, with nothing to answer",
    defaultConfig: {},
    validators: [],
    // Not a question at all: there is no answer an applicant could give it,
    // and anything claiming to be one is refused rather than stored.
    answerSchema: () => z.never() as unknown as z.ZodType<AnswerValue>,
    toText: () => "",
  },
};

export function definitionFor(type: QuestionType): QuestionTypeDefinition {
  return QUESTION_TYPE_DEFINITIONS[type];
}

/** Every type, in the order they're offered in the builder's type picker. */
export const ORDERED_QUESTION_TYPES: readonly QuestionTypeDefinition[] =
  QUESTION_TYPES.map((type) => QUESTION_TYPE_DEFINITIONS[type]);

/** Whether this type offers options at all — drives the inspector's editor. */
export function hasOptions(type: QuestionType): boolean {
  return QUESTION_TYPE_DEFINITIONS[type].defaultOptionLabels !== undefined;
}

/** Whether this type has rows as well as options — grids. */
export function hasRows(type: QuestionType): boolean {
  return QUESTION_TYPE_DEFINITIONS[type].defaultRowLabels !== undefined;
}

/**
 * Whether this question can take an "Other" answer at all.
 *
 * Both halves are load-bearing: the *type* must render an Other box (only the
 * radio and checkbox lists do — a dropdown never offers one) and the setting
 * must ask for it. A collaborative merge can strand `allowOther: true` on a
 * question retyped to dropdown, and trusting the flag alone lets an invisible
 * Other answer pass the schema and steer conditions with no control anywhere
 * that shows or clears it. Every consumer of "does this question offer
 * Other" — the answer schema, reconciliation, the renderers, the publish
 * checks — must agree, so they all come through here.
 */
export function offersOther(question: Question): boolean {
  return (
    (question.type === "multiple_choice" || question.type === "checkboxes") &&
    question.config?.allowOther === true
  );
}

/**
 * The top of a rating's scale as the control actually offers it.
 *
 * The renderer draws at most ten icons — past that a count stops reading as
 * a count — and publishing enforces 2–10, but a draft can carry any `max`
 * (retyping a linear scale keeps the setting). The schema, the repair and
 * the renderer must all mean the same thing by "the top", or a value the
 * control can't express survives as an invisible answered state.
 */
export function ratingMax(question: Question): number {
  return Math.min(Math.max(Math.round(question.config?.max ?? 5), 1), 10);
}

/** Whether this type asks the applicant for anything at all. */
export function takesAnswer(type: QuestionType): boolean {
  return type !== "text_block";
}

/**
 * From a walk through a form, the entries that have an answer at all.
 *
 * `visibleQuestions` reports what the applicant was *shown*, which includes
 * the text blocks the form uses for instructions. Anything that reads a walk
 * as answers — validating them, listing them, reporting them back — wants
 * this narrowing instead, and naming it once is what stops each caller
 * remembering `takesAnswer` on its own. One did not, and every text block on
 * the form was reported to a reviewer as an answer the applicant withheld.
 */
export function answerableQuestions(
  questions: readonly Question[],
): Question[] {
  return questions.filter((question) => takesAnswer(question.type));
}

/**
 * Whether a condition or jump can read this type's answer.
 *
 * A text block has no answer to read, and a grid's answer is a table — "the
 * answer equals this option" has no single answer to ask it of. Google draws
 * the same line. Everything that offers condition sources filters through
 * here, and publishing refuses a condition that reaches one of these anyway —
 * a concurrent type change can create what the picker won't.
 */
export function canConditionOn(type: QuestionType): boolean {
  return (
    takesAnswer(type) &&
    type !== "grid" &&
    type !== "repeatable_group" &&
    type !== "organization"
  );
}

/** Whether a validation rule is meaningful for a type — drives the inspector. */
export function supportsValidator(
  type: QuestionType,
  kind: ValidationRule["kind"],
): boolean {
  return QUESTION_TYPE_DEFINITIONS[type].validators.includes(kind);
}

/** How an answer reads as plain text, for anything downstream of the form. */
export function answerToText(
  question: Question,
  value: AnswerValue | undefined,
): string {
  return definitionFor(question.type).toText(
    visibleRepeatedAnswer(question, value),
    question,
  );
}

/**
 * An answer with references the question no longer backs removed — the repair
 * for a concurrent edit landing under a live fill.
 *
 * An answer names option, row and column ids; a collaborator can delete any
 * of those while an answer holding them sits in someone's fill session. The
 * stale reference then fails validation with nothing on screen to clear it —
 * the control only draws what the question offers now. Anything holding
 * answers while the form can change under them runs their answers through
 * here when it does.
 *
 * A number outside a scale's bounds is deliberately NOT repaired: bounds are
 * a validation matter with a visible control to fix them (an out-of-range
 * radio re-picks, a typed number edits), and the number field's partial
 * states — "2" on the way to "25" under a min of 10 — are someone typing,
 * which this must never eat.
 *
 * Keyed by the *value's* shape, not the question's type: it is the value that
 * carries the references. Deliberately not "drop whatever the schema
 * rejects" — a half-typed month-and-day is schema-invalid mid-fill, and
 * repairing a concurrent edit must never eat an answer someone is still
 * typing.
 *
 * The value's kind is itself such a reference: a question retyped underneath
 * a fill leaves an answer no control for the new type can display or clear —
 * a number on what is now a short answer renders as blank and still blocks.
 * A mismatched kind is dropped before any per-kind repair runs. Not folded
 * into "schema rejects" for the reason above: a kind mismatch is
 * unrepresentable in the new control, a schema failure may be mid-typing.
 */
/**
 * The answer kind each type's control produces — what `reconcileAnswer`
 * checks a held value against. `null` for a type that takes no answer.
 * A `Record` over the type union so a new type cannot ship without saying.
 */
const ANSWER_KIND: Record<QuestionType, AnswerValue["kind"] | null> = {
  organization: "organization",
  short_answer: "text",
  money: "text",
  paragraph: "text",
  multiple_choice: "choice",
  checkboxes: "choices",
  dropdown: "choice",
  date: "text",
  time: "text",
  file_upload: "files",
  linear_scale: "number",
  rating: "number",
  grid: "grid",
  repeatable_group: "entries",
  text_block: null,
};

/**
 * The part of a text value this question's control can show — the whole
 * text, a truncation of it, or nothing.
 *
 * Kind alone is too coarse: a short answer retyped as a date leaves "hello"
 * under a native date input, which renders it as blank while routing counts
 * it answered and validation rejects it — invisible and uncloseable. The line
 * is drawn by what each control can *emit*, so nothing mid-typing is ever
 * eaten:
 *
 * - native date / datetime / time inputs emit a complete value or "" — never
 *   a partial — so a non-matching value here cannot be someone typing;
 * - the month-day selects pad as they go, so anything `--…`-shaped is kept,
 *   schema-valid or not;
 * - duration and the plain text types are free-typed, so everything is kept.
 *
 * Where the undisplayable part is a *suffix* — a time written before "and
 * time" was turned off — the displayable prefix is kept rather than the whole
 * pick being eaten: the month, day or date the applicant chose is still
 * exactly what the control shows.
 */
function displayableText(question: Question, text: string): string | null {
  if (question.type === "date") {
    const includeTime = question.config?.includeTime === true;
    if (question.config?.monthDayOnly === true) {
      if (!text.startsWith("--")) return null;
      if (includeTime) return text;
      const timeStart = text.indexOf("T");
      return timeStart === -1 ? text : text.slice(0, timeStart);
    }
    // Real calendar validity, not a digit pattern: a native input renders
    // `2026-99-99` as blank exactly like `hello`, and "what the control can
    // display" and "what it can emit" are the same set — valid values only.
    if (includeTime) {
      return z.iso.datetime({ local: true }).safeParse(text).success
        ? text
        : null;
    }
    if (z.iso.date().safeParse(text).success) return text;
    const dateHalf = text.slice(0, 10);
    return /T/.test(text) && z.iso.date().safeParse(dateHalf).success
      ? dateHalf
      : null;
  }
  if (question.type === "time" && question.config?.duration !== true) {
    return z.iso.time().safeParse(text).success ? text : null;
  }
  return text;
}

export function reconcileAnswer(
  question: Question,
  value: AnswerValue | undefined,
): AnswerValue | undefined {
  if (value === undefined) return undefined;
  if (value.kind !== ANSWER_KIND[question.type]) return undefined;
  switch (value.kind) {
    case "organization":
      return value;
    case "text": {
      const shown = displayableText(question, value.value);
      if (shown === null) return undefined;
      return shown === value.value ? value : { kind: "text", value: shown };
    }
    case "entries": {
      const fields = repeatFieldsOf(question);
      const entries = value.entries.map((entry) => {
        const values = Object.fromEntries(
          fields.flatMap((field) => {
            const original = entry.values[field.id];
            if (original === undefined) return [];
            const reconciled = reconcileAnswer(
              fieldQuestion(field),
              entryValue(entry, field.id),
            );
            return reconciled && reconciled.kind !== "entries"
              ? [
                  [
                    field.id,
                    typeof original === "string" && reconciled.kind === "text"
                      ? reconciled.value
                      : reconciled,
                  ],
                ]
              : [];
          }),
        );
        const unchanged =
          Object.keys(values).length === Object.keys(entry.values).length &&
          Object.entries(values).every(
            ([id, text]) => entry.values[id] === text,
          );
        return unchanged ? entry : { ...entry, values };
      });
      return entries.every((entry, index) => entry === value.entries[index])
        ? value
        : { kind: "entries", entries };
    }
    case "files":
      // File ids are references the server resolves against the file records
      // it owns; nothing here can tell a live one from a stale one.
      return value;
    case "number": {
      // A rating is tap-only — no control of it ever holds a mid-typed
      // number — so a value beyond a shrunken `max` is a stale reference
      // like a deleted option id: kept, it renders every icon filled with
      // nothing checked and steers `isAnswered` conditions invisibly. The
      // linear scale is different: its oversized-range fallback is a typed
      // field, and a typed number passes through out-of-range states, so
      // scale bounds stay validation's business.
      if (question.type === "rating") {
        const max = ratingMax(question);
        if (value.value < 1 || value.value > max) return undefined;
      }
      return value;
    }
    case "choice": {
      if (value.choice.kind === "other") {
        return offersOther(question) ? value : undefined;
      }
      // Membership in `optionsOf`, never `optionById`: the raw config still
      // holds an option whose label was blanked, but nothing renders or
      // accepts it — "offered" must mean the same thing here as it does to
      // the controls and the answer schema.
      const pickedId = value.choice.optionId;
      return optionsOf(question).some((option) => option.id === pickedId)
        ? value
        : undefined;
    }
    case "choices": {
      const offered = new Set(optionsOf(question).map((option) => option.id));
      const kept = value.choices.filter((choice) =>
        choice.kind === "other"
          ? offersOther(question)
          : offered.has(choice.optionId),
      );
      if (kept.length === 0) return undefined;
      return kept.length === value.choices.length
        ? value
        : { kind: "choices", choices: kept };
    }
    case "grid": {
      const rowIds = new Set(rowsOf(question).map((row) => row.id));
      const columnIds = new Set(optionsOf(question).map((option) => option.id));
      const multi = question.config?.multiSelect === true;
      const rows = value.rows
        .filter((row) => rowIds.has(row.rowId))
        .map((row) => {
          const kept = row.optionIds.filter((id) => columnIds.has(id));
          return {
            rowId: row.rowId,
            // A grid switched from multi- to single-select shows one pick per
            // row — the first — while extra ids would fail validation from
            // behind it. Keep exactly what the control shows, same principle
            // as truncating a stale time suffix.
            optionIds: multi ? kept : kept.slice(0, 1),
          };
        })
        .filter((row) => row.optionIds.length > 0);
      if (rows.length === 0) return undefined;
      const untouched =
        rows.length === value.rows.length &&
        rows.every(
          (row, index) =>
            row.optionIds.length === value.rows[index]!.optionIds.length,
        );
      return untouched ? value : { kind: "grid", rows };
    }
  }
}
